import React, { useState } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  normalizePhone,
  sanitizeText,
  validateCountryCode,
  validatePassword
} from '../utils/inputValidation';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
    confirmPassword: '',
    country_code: '',
    country_name: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateForm = () => {
    let newErrors = {};
    if (!formData.phone.trim()) newErrors.phone = 'El número es obligatorio.';
    else if (!/^\+?[0-9]{7,15}$/.test(formData.phone)) newErrors.phone = 'Formato inválido.';
    if (!formData.password) newErrors.password = 'La contraseña es obligatoria.';
    else if (formData.password.length < 6) newErrors.password = 'Debe tener al menos 6 caracteres.';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Las contraseñas no coinciden.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');
    if (!validateForm()) return;

    // 🔥 CAPA 2 ANTI-SPAM: Verificación local antes de llamar a la base de datos
    const today = new Date().toDateString();
    const savedDate = localStorage.getItem('otp_date');
    let localAttempts = parseInt(localStorage.getItem('otp_attempts') || '0');

    if (savedDate !== today) {
      localAttempts = 0;
      localStorage.setItem('otp_date', today);
    }

    if (localAttempts >= 3) {
      setMessage('Límite de seguridad: Intenta de nuevo en 24 horas.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const safePhone = normalizePhone(formData.phone);
      const safePassword = validatePassword(formData.password);
      const safeCountryCode = validateCountryCode(formData.country_code, 'País') || '';
      const safeCountryName = sanitizeText(formData.country_name, {
        fieldName: 'País',
        maxLength: 80
      });

      const { data: isRegistered, error: checkError } = await supabase.rpc('phone_is_registered', {
        p_phone: safePhone
      });

      if (checkError) throw checkError;

      if (isRegistered) {
        setMessage('Ya tienes una cuenta registrada. Inicia sesión.');
        setMessageType('success');
        setLoading(false);
        setTimeout(() => {
          navigate('/login', { state: { phone: formData.phone } });
        }, 1500);
        return;
      }

      // 2. Guardar datos temporales para el flujo de registro
      localStorage.setItem('pendingPassword', safePassword);
      localStorage.setItem('rawPasswordForLogin', safePassword);

      const { error: otpError } = await supabase.rpc('request_registration_otp', {
        p_phone: safePhone,
        p_password: safePassword,
        p_country_code: safeCountryCode,
        p_country_name: safeCountryName
      });

      if (otpError) {
        if (otpError.message?.includes('PHONE_ALREADY_REGISTERED')) {
          throw new Error('Ya tienes una cuenta registrada. Inicia sesión.');
        }
        if (otpError.code === '23505') {
          throw new Error('Ya enviamos un código de seguridad. Por favor, espera 15 minutos antes de solicitar uno nuevo.');
        }
        
        // 🔥 Error del Trigger Guardián (Límite de 3 peticiones en la BD)
        if (otpError.message?.includes('Límite de seguridad') || otpError.code === 'P0001') {
          localStorage.setItem('otp_attempts', '3'); // Bloqueo local inmediato
          throw new Error('Has superado el límite diario de códigos para este número.');
        }
        
        throw otpError;
      }

      // 4. Si todo sale bien, incrementar el contador local
      localStorage.setItem('otp_attempts', (localAttempts + 1).toString());

      setMessage(`¡Registro exitoso! Te hemos enviado un código a WhatsApp.`);
      setMessageType('success');
      
      setTimeout(() => {
        navigate('/verify-code', { state: { phone: formData.phone } });
      }, 1500);

    } catch (error) {
      console.error('Error:', error);
      setMessage(error.message || 'Error al generar código. Intenta nuevamente.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout showNavigation={false}>
      <LayoutGroup>
        <div className="min-h-screen bg-[#f4f7fb] flex flex-col px-5 py-6 md:py-8 relative overflow-hidden">
          
          <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-blue-900/5 to-transparent pointer-events-none"></div>

          <motion.div
            className="relative z-10 flex items-center mb-4 max-w-md mx-auto w-full"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors shadow-sm active:scale-95"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </motion.div>

          <motion.div
            className="relative z-10 w-full max-w-md mx-auto flex-1 flex flex-col pb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            
            <div className="text-center mb-5 flex flex-col items-center">
              <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4 md:px-8 md:py-5 rounded-[28px] md:rounded-[32px] shadow-[0_15px_30px_rgba(30,58,138,0.25)] flex items-center gap-4 md:gap-5">
                <motion.img
                  layoutId="logo-mark"
                  src="/logo-icono.png" 
                  alt="Logo Icono"
                  className="w-20 h-20 md:w-20 md:h-20 object-contain rounded-xl"
                />
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                  className="flex flex-col text-left"
                >
                  <span className="text-4xl md:text-5xl font-black tracking-tight leading-none text-white pb-0.5">
                    Remesas
                  </span>
                  <span className="text-4xl md:text-5xl font-black tracking-tight leading-none text-blue-200">
                    Venezuela
                  </span>
                </motion.div>
              </div>

              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-slate-500 font-medium mt-4 text-sm"
              >
                Crea tu cuenta en segundos
              </motion.p>
            </div>

            <div className="bg-white rounded-[32px] shadow-sm border border-slate-100 p-6 md:p-8">
              
              <AnimatePresence mode="wait">
                {message && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, y: -10 }}
                    animate={{ opacity: 1, height: 'auto', y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mb-6"
                  >
                    <div className={`flex items-center gap-3 p-4 rounded-2xl border ${
                      messageType === 'success'
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                      {messageType === 'success' ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
                      <p className="text-sm font-bold tracking-tight leading-tight">{message}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="relative">
                  <label className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">
                    Número de Teléfono
                  </label>
                  <PhoneInput
                    country="co"
                    value={formData.phone}
                    onChange={(phone, countryData) => setFormData({
                      ...formData,
                      phone: phone.startsWith('+') ? phone : `+${phone}`,
                      country_code: countryData?.countryCode?.toUpperCase() || '',
                      country_name: countryData?.name || ''
                    })}
                    inputStyle={{
                      width: '100%',
                      borderRadius: '16px',
                      height: '56px',
                      paddingLeft: '60px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#1e293b'
                    }}
                    buttonStyle={{ 
                      borderRadius: '16px 0 0 16px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: 'transparent',
                      paddingLeft: '8px'
                    }}
                    enableSearch
                    placeholder="Tu número de WhatsApp"
                    disabled={loading}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs font-bold mt-1.5 ml-1">{errors.phone}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">
                    Contraseña
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="••••••••"
                      className="w-full pl-12 pr-14 py-4 h-[56px] bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all font-semibold text-slate-800 placeholder:text-slate-400 tracking-wide"
                      disabled={loading}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs font-bold mt-1.5 ml-1">{errors.password}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 block ml-1">
                    Confirmar Contraseña
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      className="w-full pl-12 pr-14 py-4 h-[56px] bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-600 transition-all font-semibold text-slate-800 placeholder:text-slate-400 tracking-wide"
                      disabled={loading}
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs font-bold mt-1.5 ml-1">{errors.confirmPassword}</p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  className={`w-full py-4 mt-6 rounded-2xl font-black text-white text-[15px] uppercase tracking-wider transition-all duration-300 shadow-[0_10px_20px_rgba(30,58,138,0.15)] hover:shadow-[0_15px_25px_rgba(30,58,138,0.25)] active:scale-[0.98] ${
                    loading ? 'bg-blue-800 cursor-not-allowed' : 'bg-blue-900 hover:bg-blue-800'
                  }`}
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Procesando...
                    </div>
                  ) : (
                    'Crear Cuenta'
                  )}
                </motion.button>
              </form>

              <div className="mt-8 text-center pt-6 border-t border-slate-100">
                <p className="text-sm font-medium text-slate-500">
                  ¿Ya tienes una cuenta?{' '}
                  <button 
                    onClick={() => navigate('/login')}
                    type="button"
                    className="font-bold text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    Inicia Sesión
                  </button>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </LayoutGroup>
    </Layout>
  );
};

export default Register;

